import type { GlideFC } from "lib/glide-fc";
import React from "react";
import { createPortal } from "react-dom";

interface Props {
    selector: string;
}

export const PortalObserver: GlideFC<React.PropsWithChildren<Props>> = p => {
    const { selector, children } = p;
    const [element, setElement] = React.useState<HTMLElement>();

    const mounted = React.useRef(false);
    React.useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        setElement(undefined);
    }, [selector]);

    React.useEffect(() => {
        if (element === undefined) {
            const el = document.querySelector(selector);
            if (el instanceof HTMLElement) {
                setElement(el);
                return undefined;
            } else {
                // watch for element to arrive
                const observer = new MutationObserver(records => {
                    for (const record of records) {
                        for (let i = 0; i < record.addedNodes.length; i++) {
                            const added = record.addedNodes.item(i);
                            if (added instanceof HTMLElement && added.matches(selector)) {
                                setElement(added);
                                return;
                            }
                        }
                    }
                });
                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                });
                return () => {
                    observer.disconnect();
                };
            }
        } else {
            // watch for element to leave
            const observer = new MutationObserver(records => {
                for (const record of records) {
                    for (let i = 0; i < record.removedNodes.length; i++) {
                        const removed = record.removedNodes.item(i);
                        if (removed === element) {
                            setElement(undefined);
                            return;
                        }
                    }
                }
            });
            observer.observe(element.parentElement ?? document.body, {
                childList: true,
            });
            return () => {
                observer.disconnect();
            };
        }
    }, [element, selector]);

    if (element === undefined) return null;

    return createPortal(children, element);
};
